import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NbThemeService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import 'moment/locale/it';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { Subject } from 'rxjs';
import { filter, first, takeUntil, tap } from 'rxjs/operators';
import { BASE_HREF } from '../base-href';
import { DEPLOY_URL } from '../deploy-url';
import {
  NGX_SESSION_STORAGE_KEYS,
  PRENO_LOCAL_STORAGE_KEYS,
  STORAGE_KEYS,
} from './modules/shared/constants';
import { TextChangeService } from './modules/shared/services/text-change.service';
import { Title } from '@angular/platform-browser';
import { StepperFormService } from './modules/shared/services';

@Component({
  selector: 'ngx-app',
  styles: [],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroyed = new Subject<void>();
  private cookiePolicyUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(DEPLOY_URL) public deployUrl,
    @Inject(BASE_HREF) public baseHref,
    private ccService: NgcCookieConsentService,
    private translateService: TranslateService,
    private readonly router: Router,
    private titleService: Title,
    private readonly formService: StepperFormService
  ) {
    // if (sessionStorage.getItem('theme')) {
    //   this.themeService.changeTheme(sessionStorage.getItem('theme'));
    // }

    this.cookiePolicyUrl = baseHref + 'public/cookie-policy';

    if (localStorage.getItem('darkMode') === 'true') {
      const root = document.documentElement;
      root.style.setProperty('--card-bg', '#1a1a2e');
      root.style.setProperty('--card-text', '#e0e0e0');
      root.style.setProperty('--input-bg', '#16213e');
      root.style.setProperty('--card-link', '#e57388');
      root.style.setProperty('--confirm-bar-border', '#2a3a5c');
    } else {
      const root = document.documentElement;
      root.style.removeProperty('--card-bg');
      root.style.removeProperty('--card-text');
      root.style.removeProperty('--input-bg');
      root.style.removeProperty('--card-link');
      root.style.removeProperty('--confirm-bar-border');
    }
  }

  ngOnInit() {
    this.router.events
      .pipe(
        takeUntil(this.destroyed),
        filter((event) => event instanceof NavigationEnd),
        filter(({ url }: NavigationEnd) => url === '/auth/logout'),
        tap(() => {
          for (const key of Object.values(STORAGE_KEYS.local)) {
            localStorage.removeItem(key);
          }

          for (const key of Object.values(STORAGE_KEYS.session)) {
            sessionStorage.removeItem(key);
          }

          this.formService.resetForm();
        })
      )
      .subscribe();

    // Support for translated cookies messages
    const lang = this.translateService.currentLang || 'it';
    moment.locale(lang);

    this.updateCookieBar();
    this.translateService.onLangChange.subscribe(() => this.updateCookieBar());

    const expirationDate = localStorage.getItem(
      PRENO_LOCAL_STORAGE_KEYS.expirationDate
    )
      ? moment(
          localStorage.getItem(PRENO_LOCAL_STORAGE_KEYS.expirationDate)
        ).isValid()
        ? moment(
            localStorage.getItem(PRENO_LOCAL_STORAGE_KEYS.expirationDate)
          ).toISOString()
        : null
      : null;

    if (
      !expirationDate ||
      (expirationDate && moment().isAfter(moment(expirationDate)))
    ) {
      for (const key of Object.values(STORAGE_KEYS.local)) {
        localStorage.removeItem(key);
      }

      for (const key of Object.keys(STORAGE_KEYS.session).filter(
        (k) => k !== NGX_SESSION_STORAGE_KEYS.fiscalCode
      )) {
        sessionStorage.removeItem(key);
      }
    }
  }

  private updateCookieBar(): void {
    this.translateService.get(['cookie.message', 'cookie.dismiss', 'cookie.link']).subscribe(data => {
      const config = this.ccService.getConfig();
      config.content = config.content || {};
      config.content.message = data['cookie.message'];
      config.content.dismiss = data['cookie.dismiss'];
      config.content.cookiePolicyLink = data['cookie.link'];
      config.content.cookiePolicyHref = this.cookiePolicyUrl;
      config.autoAttach = true;
      this.ccService.destroy();
      this.ccService.init(config);
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
